import { useState } from "react";
import { Store } from "@/types";
import { Plus, Edit2, Trash2, Store as StoreIcon, MapPin, Phone, RefreshCw, Download } from "lucide-react";
import { api } from "@/lib/firebase/api-client";
import { ConfirmModal } from "@/components/shared/ConfirmModal";
import { AlertModal } from "@/components/shared/AlertModal";
import { useModal } from "@/hooks/useModal";
import Loading from "@/components/shared/Loading";
import { demiHeures, heureHHMM } from "@/lib/utils/heure";
import { 
  useStores, 
  useCreateStore, 
  useUpdateStore, 
  useDeleteStore,
  useSyncStoreProducts 
} from "@/lib/hooks/queries";

export default function StoresPage() {
  // ✅ React Query remplace useState + useEffect
  const { data: stores = [], isLoading } = useStores();

  // Le magasin dont l'export est en cours. Un seul a la fois : deux clics
  // sur le meme bouton lanceraient deux telechargements identiques.
  const [exportEnCours, setExportEnCours] = useState<string | null>(null);
  
  // Mutations
  const createStoreMutation = useCreateStore();
  const updateStoreMutation = useUpdateStore();
  const deleteStoreMutation = useDeleteStore();
  const syncProductsMutation = useSyncStoreProducts();

  const [showForm, setShowForm] = useState(false);
  const [editingStore, setEditingStore] = useState<Store | null>(null);
  const [syncingStoreId, setSyncingStoreId] = useState<string | null>(null);
  const [syncingStoreName, setSyncingStoreName] = useState<string | null>(null);
  const [syncProgress, setSyncProgress] = useState({ percentage: 0, status: "" });
  const [formData, setFormData] = useState({
    name: "",
    address: "",
    phone: "",
    twilioNumber: "",
    glovoStoreId: "",
    glovoStoreCode: "",
    heureOuverture: 9,
    heureFermeture: 21,
    ferméLeVendredi: true,
    isActive: true,
  });

  // Modales
  const deleteModal = useModal();
  const successModal = useModal();
  const errorModal = useModal();
  const [deleteStore, setDeleteStore] = useState<{ id: string; name: string } | null>(null);
  const [modalMessage, setModalMessage] = useState({ title: "", message: "" });

  // Réinitialiser le formulaire
  function resetForm() {
    setFormData({
      name: "",
      address: "",
      phone: "",
      twilioNumber: "",
      glovoStoreId: "",
      glovoStoreCode: "",
      heureOuverture: 9,
      heureFermeture: 21,
      ferméLeVendredi: true,
      isActive: true,
    });
    setEditingStore(null);
    setShowForm(false);
  }

  // Ouvrir formulaire de création
  function handleCreate() {
    resetForm();
    setShowForm(true);
  }

  // Ouvrir formulaire d'édition
  function handleEdit(store: Store) {
    setFormData({
      name: store.name,
      address: store.address || "",
      phone: store.phone || "",
      twilioNumber: store.twilioNumber || "",
      glovoStoreId: (store as any).glovoStoreId || "",
      glovoStoreCode: (store as any).glovoStoreCode || "",
      heureOuverture: (store as any).heureOuverture ?? 9,
      heureFermeture: (store as any).heureFermeture ?? 21,
      ferméLeVendredi: Array.isArray((store as any).joursFermes)
        ? (store as any).joursFermes.includes(5)
        : true,
      isActive: store.isActive ?? true,
    });
    setEditingStore(store);
    setShowForm(true);
  }

  // Soumettre le formulaire
  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();

    // Une case a cocher se lit mieux qu'un tableau de numeros de jours,
    // mais c'est le tableau que la base attend. On traduit ici, au seul
    // endroit ou les deux se rencontrent.
    const donnees: any = {
      ...formData,
      joursFermes: (formData as any).ferméLeVendredi ? [5] : [],
    };
    delete donnees.ferméLeVendredi;

    try {
      if (editingStore) {
        await updateStoreMutation.mutateAsync({ id: editingStore.id, data: donnees });
        setModalMessage({
          title: "Succès",
          message: "Store modifié avec succès",
        });
        successModal.open();
      } else {
        await createStoreMutation.mutateAsync(donnees);
        setModalMessage({
          title: "Succès",
          message: "Store créé avec succès",
        });
        successModal.open();
      }
      resetForm();
    } catch (error: any) {
      console.error("Error saving store:", error);
      setModalMessage({
        title: "Erreur",
        message: error.message || "Erreur lors de la sauvegarde",
      });
      errorModal.open();
    }
  }

  // Ouvrir modal de suppression
  function handleDeleteClick(store: Store) {
    setDeleteStore({ id: store.id, name: store.name });
    deleteModal.open();
  }

  // Confirmer suppression
  async function handleDeleteConfirm() {
    if (!deleteStore) return;

    try {
      await deleteStoreMutation.mutateAsync(deleteStore.id);
      deleteModal.close();
      setModalMessage({
        title: "Succès",
        message: "Store supprimé avec succès",
      });
      successModal.open();
    } catch (error: any) {
      console.error("Error deleting store:", error);
      deleteModal.close();
      setModalMessage({
        title: "Erreur",
        message: error.message || "Erreur lors de la suppression",
      });
      errorModal.open();
    }
  }

  // Synchroniser les produits depuis Glovo
  async function handleSyncProducts(store: Store) {
    if (!store.glovoStoreId) {
      setModalMessage({
        title: "Erreur",
        message: "Ce store n'a pas de glovoStoreId configuré. Veuillez d'abord configurer l'ID Glovo du store.",
      });
      errorModal.open();
      return;
    }

    try {
      setSyncingStoreId(store.id);
      setSyncingStoreName(store.name);
      setSyncProgress({ percentage: 0, status: "Connexion à l'API Glovo..." });

      // Simuler la progression (comme dans l'ancien projet)
      const progressInterval = setInterval(() => {
        setSyncProgress((prev) => {
          if (prev.percentage >= 95) return prev; // Cap à 95% jusqu'à la réponse réelle
          const newPercentage = Math.min(prev.percentage + 5, 95);
          let newStatus = "";
          if (newPercentage < 30) {
            newStatus = "Récupération des produits depuis Glovo...";
          } else if (newPercentage < 60) {
            newStatus = "Traitement des données...";
          } else {
            newStatus = "Sauvegarde dans la base de données...";
          }
          return { percentage: newPercentage, status: newStatus };
        });
      }, 500);

      const result = await syncProductsMutation.mutateAsync(store.id);
      
      clearInterval(progressInterval);
      
      // Compléter la progression
      setSyncProgress({
        percentage: 100,
        status: "Synchronisation terminée!",
      });

      // Attendre un peu pour montrer le 100%
      await new Promise(resolve => setTimeout(resolve, 800));

      // Calculer les stats correctement
      const totalCreated = (result.stats?.global?.created || 0) + (result.stats?.store?.created || 0);
      const totalUpdated = (result.stats?.global?.updated || 0) + (result.stats?.store?.updated || 0);
      
      setModalMessage({
        title: "Succès",
        message: `${result.message || "Synchronisation terminée"}. ${totalCreated} produits créés, ${totalUpdated} produits mis à jour.`,
      });
      successModal.open();
    } catch (error: any) {
      console.error("Error syncing products:", error);
      setModalMessage({
        title: "Erreur",
        message: error.message || "Erreur lors de la synchronisation des produits",
      });
      errorModal.open();
    } finally {
      setSyncingStoreId(null);
      setSyncingStoreName(null);
      setSyncProgress({ percentage: 0, status: "" });
    }
  }

  if (isLoading) {
    return (
      <div className="p-4 md:p-6">
        <Loading />
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 relative">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-4 md:mb-6">
        <h1 className="text-xl md:text-2xl font-bold">Gestion des Stores</h1>
        <button
          onClick={handleCreate}
          className="bg-primary text-white px-4 py-2 rounded hover:bg-primary/90 flex items-center justify-center gap-2 min-h-[44px] w-full sm:w-auto"
        >
          <Plus className="w-5 h-5" />
          <span>Nouveau Store</span>
        </button>
      </div>

      {/* Formulaire */}
      {showForm && (
        <div className="bg-white rounded-xl shadow-lg border border-gray-200 mb-4 md:mb-6 overflow-hidden">
          {/* Header du formulaire */}
          <div className="bg-gradient-to-r from-primary/10 to-primary/5 px-5 md:px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg md:text-xl font-bold text-gray-800 flex items-center gap-2">
              <StoreIcon className="w-5 h-5 text-primary" />
              {editingStore ? "Modifier le Store" : "Nouveau Store"}
            </h2>
          </div>
          
          {/* Contenu du formulaire */}
          <form onSubmit={handleSubmit} className="p-5 md:p-6 space-y-5">
            {/* Section Informations principales */}
            <div className="space-y-4 pb-4 border-b border-gray-100">
              <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">
                Informations principales
              </h3>
              <div>
                <label className="block text-sm font-medium mb-2 text-gray-700">
                  Nom du Store <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all min-h-[44px]"
                  placeholder="Ex: Store Casablanca Maarif"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2 text-gray-700">Adresse</label>
                <input
                  type="text"
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all min-h-[44px]"
                  placeholder="Ex: 123 Rue Example, Casablanca"
                />
              </div>
            </div>

            {/* Section Contact */}
            <div className="space-y-4 pb-4 border-b border-gray-100">
              <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">
                Contact
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2 text-gray-700">Téléphone</label>
                  <input
                    type="text"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all min-h-[44px]"
                    placeholder="+212600000000"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2 text-gray-700">
                    Numéro Twilio
                  </label>
                  <input
                    type="text"
                    value={formData.twilioNumber}
                    onChange={(e) =>
                      setFormData({ ...formData, twilioNumber: e.target.value })
                    }
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all min-h-[44px]"
                    placeholder="+1234567890"
                  />
                </div>
              </div>
            </div>

            {/* Section Intégrations */}
            <div className="space-y-4 pb-4 border-b border-gray-100">
              <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">
                Intégrations
              </h3>
              <div>
                <label className="block text-sm font-medium mb-2 text-gray-700">
                  ID Glovo CATALOGUE (DH / v2)
                </label>
                <input
                  type="text"
                  value={formData.glovoStoreId}
                  onChange={(e) =>
                    setFormData({ ...formData, glovoStoreId: e.target.value })
                  }
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all min-h-[44px] font-mono text-sm"
                  placeholder="Ex: 588581 (vendorId catalogue)"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2 text-gray-700">
                  ID Glovo COMMANDES (v0 / marketplace)
                </label>
                <input
                  type="text"
                  value={(formData as any).glovoStoreCode}
                  onChange={(e) =>
                    setFormData({ ...formData, glovoStoreCode: e.target.value })
                  }
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all min-h-[44px] font-mono text-sm"
                  placeholder="Ex: store-01, store-02, store-03"
                />
              </div>
            </div>

            {/* Section Horaires de vente */}
            <div className="space-y-4">
              <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">
                Horaires de vente
              </h3>
              <p className="text-xs text-gray-500 -mt-2">
                Ces heures bornent les créneaux proposés aux fournisseurs
                quand ils déclarent une rupture. On ne leur propose jamais
                de remettre un produit en vente après la fermeture.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2 text-gray-700">
                    Ouverture
                  </label>
                  <select
                    value={(formData as any).heureOuverture}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        heureOuverture: Number(e.target.value),
                      })
                    }
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg min-h-[44px]"
                  >
                    {demiHeures(0, 23.5).map((h) => (
                      <option key={h} value={h}>
                        {heureHHMM(h).replace(":", "h")}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2 text-gray-700">
                    Fermeture
                  </label>
                  <select
                    value={(formData as any).heureFermeture}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        heureFermeture: Number(e.target.value),
                      })
                    }
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg min-h-[44px]"
                  >
                    {demiHeures(0.5, 24).map((h) => (
                      <option key={h} value={h}>
                        {heureHHMM(h).replace(":", "h")}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={(formData as any).ferméLeVendredi}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      ferméLeVendredi: e.target.checked,
                    })
                  }
                  className="w-5 h-5 rounded border-gray-300"
                />
                <span className="text-sm text-gray-700">
                  Fermé le vendredi
                </span>
              </label>
            </div>

            {/* Section Statut */}
            <div className="space-y-4">
              <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">
                Statut
              </h3>
              <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg border border-gray-200">
                <input
                  type="checkbox"
                  id="isActive"
                  checked={formData.isActive}
                  onChange={(e) =>
                    setFormData({ ...formData, isActive: e.target.checked })
                  }
                  className="w-5 h-5 text-primary border-gray-300 rounded focus:ring-primary focus:ring-2"
                />
                <label htmlFor="isActive" className="text-sm font-medium text-gray-700 cursor-pointer">
                  Store actif
                </label>
              </div>
            </div>

            {/* Actions */}
            <div className="flex flex-col sm:flex-row gap-3 pt-6 border-t border-gray-200">
              <button
                type="submit"
                className="flex-1 bg-primary text-white px-6 py-3 rounded-lg hover:bg-primary/90 min-h-[44px] font-semibold transition-colors duration-200 shadow-sm hover:shadow-md"
              >
                {editingStore ? "Modifier le Store" : "Créer le Store"}
              </button>
              <button
                type="button"
                onClick={resetForm}
                className="flex-1 sm:flex-initial bg-white text-gray-700 px-6 py-3 rounded-lg border border-gray-300 hover:bg-gray-50 min-h-[44px] font-medium transition-colors duration-200"
              >
                Annuler
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Liste des stores - Mobile: Cards */}
      <div className="md:hidden space-y-4">
        {stores.length === 0 ? (
          <div className="bg-white p-8 rounded-lg shadow-md text-center text-gray-500">
            <StoreIcon className="w-12 h-12 mx-auto mb-4 text-gray-400" />
            <p>Aucun store trouvé</p>
          </div>
        ) : (
          stores.map((store) => (
            <div 
              key={store.id} 
              className="bg-white rounded-xl shadow-md hover:shadow-lg transition-all duration-200 overflow-hidden border border-gray-100"
            >
              {/* Header avec icône et statut */}
              <div className="bg-gradient-to-r from-primary/10 to-primary/5 px-5 py-4 border-b border-gray-100">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="bg-primary/20 p-2.5 rounded-lg">
                      <StoreIcon className="w-5 h-5 text-primary" />
                    </div>
                    <h3 className="font-bold text-lg text-gray-800">{store.name}</h3>
                  </div>
                  <span
                    className={`px-3 py-1.5 rounded-full text-xs font-semibold flex-shrink-0 ${
                      store.isActive
                        ? "bg-green-100 text-green-700 border border-green-200"
                        : "bg-red-100 text-red-700 border border-red-200"
                    }`}
                  >
                    {store.isActive ? "Actif" : "Inactif"}
                  </span>
                </div>
              </div>

              {/* Contenu */}
              <div className="px-5 py-4 space-y-3">
                {store.address && (
                  <div className="flex items-start gap-3 text-sm">
                    <MapPin className="w-4 h-4 mt-0.5 flex-shrink-0 text-gray-400" />
                    <span className="text-gray-700 flex-1">{store.address}</span>
                  </div>
                )}
                {store.phone && (
                  <div className="flex items-center gap-3 text-sm">
                    <Phone className="w-4 h-4 flex-shrink-0 text-gray-400" />
                    <span className="text-gray-700">{store.phone}</span>
                  </div>
                )}
                {store.glovoStoreId && (
                  <div className="flex items-center gap-3 text-sm">
                    <span className="text-gray-400 text-xs font-medium">Glovo ID:</span>
                    <span className="text-gray-600 font-mono text-xs bg-gray-50 px-2 py-1 rounded">
                      {store.glovoStoreId}
                    </span>
                  </div>
                )}
              </div>

              {/* Actions */}
              <div className="px-5 py-4 bg-gray-50 border-t border-gray-100 flex flex-col gap-2">
                <div className="flex gap-2">
                  <button
                    onClick={() => handleEdit(store)}
                    className="flex-1 flex items-center justify-center gap-2 bg-white text-blue-600 hover:text-blue-700 hover:bg-blue-50 text-sm py-2.5 border border-blue-200 rounded-lg min-h-[44px] font-medium transition-colors duration-200"
                  >
                    <Edit2 className="w-4 h-4" />
                    Modifier
                  </button>
                  <button
                    onClick={() => handleDeleteClick(store)}
                    className="flex-1 flex items-center justify-center gap-2 bg-white text-red-600 hover:text-red-700 hover:bg-red-50 text-sm py-2.5 border border-red-200 rounded-lg min-h-[44px] font-medium transition-colors duration-200"
                  >
                    <Trash2 className="w-4 h-4" />
                    Supprimer
                  </button>
                </div>
                {store.glovoStoreId && (
                  <button
                    onClick={() => handleSyncProducts(store)}
                    disabled={syncingStoreId === store.id}
                    className="w-full flex items-center justify-center gap-2 bg-primary text-white hover:bg-primary/90 disabled:bg-gray-300 disabled:cursor-not-allowed text-sm py-2.5 rounded-lg min-h-[44px] font-medium transition-colors duration-200"
                  >
                    <RefreshCw className={`w-4 h-4 ${syncingStoreId === store.id ? "animate-spin" : ""}`} />
                    {syncingStoreId === store.id ? "Synchronisation..." : "Synchroniser produits Glovo"}
                  </button>
                )}
              </div>
            </div>
          ))
        )}
      </div>

      {/* Desktop: Table */}
      <div className="hidden md:block bg-white rounded-xl shadow-md overflow-hidden border border-gray-100">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gradient-to-r from-gray-50 to-gray-100 border-b border-gray-200">
              <tr>
                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Nom</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Adresse</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Téléphone</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Statut</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {stores.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-6 py-12 text-center text-gray-500">
                    <StoreIcon className="w-12 h-12 mx-auto mb-3 text-gray-400" />
                    <p>Aucun store trouvé</p>
                  </td>
                </tr>
              ) : (
                stores.map((store) => (
                  <tr 
                    key={store.id} 
                    className="hover:bg-gray-50 transition-colors duration-150"
                  >
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="bg-primary/10 p-2 rounded-lg">
                          <StoreIcon className="w-4 h-4 text-primary" />
                        </div>
                        <span className="font-medium text-gray-800">{store.name}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      {store.address ? (
                        <div className="flex items-center gap-2 text-sm text-gray-600">
                          <MapPin className="w-4 h-4 text-gray-400" />
                          <span>{store.address}</span>
                        </div>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      {store.phone ? (
                        <div className="flex items-center gap-2 text-sm text-gray-600">
                          <Phone className="w-4 h-4 text-gray-400" />
                          <span>{store.phone}</span>
                        </div>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <span
                        className={`px-3 py-1.5 rounded-full text-xs font-semibold ${
                          store.isActive
                            ? "bg-green-100 text-green-700 border border-green-200"
                            : "bg-red-100 text-red-700 border border-red-200"
                        }`}
                      >
                        {store.isActive ? "Actif" : "Inactif"}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex flex-col gap-2">
                        <div className="flex gap-3">
                          <button
                            onClick={() => handleEdit(store)}
                            className="text-blue-600 hover:text-blue-700 hover:bg-blue-50 px-3 py-1.5 rounded-lg text-sm flex items-center gap-1.5 font-medium transition-colors duration-200"
                          >
                            <Edit2 className="w-4 h-4" />
                            Modifier
                          </button>
                          <button
                            onClick={() => handleDeleteClick(store)}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50 px-3 py-1.5 rounded-lg text-sm flex items-center gap-1.5 font-medium transition-colors duration-200"
                          >
                            <Trash2 className="w-4 h-4" />
                            Supprimer
                          </button>
                        </div>
                        {/* Le rapprochement : on va lire le catalogue REEL
                            chez Glovo et on le compare au notre. Rien n'est
                            ecrit — on constate, et on rend un fichier. */}
                        <button
                          onClick={async () => {
                            setExportEnCours(store.id);
                            try {
                              await api.stores.telechargerConcordance(store.id);
                            } catch (e: any) {
                              setModalMessage({
                                title: "Concordance impossible",
                                message: e?.message || "Erreur inconnue",
                              });
                              errorModal.open();
                            } finally {
                              setExportEnCours(null);
                            }
                          }}
                          disabled={exportEnCours === store.id}
                          className="text-gray-700 hover:text-gray-900 hover:bg-gray-50 disabled:text-gray-400 disabled:cursor-not-allowed px-3 py-1.5 rounded-lg text-sm flex items-center gap-1.5 font-medium transition-colors duration-200 border border-gray-300"
                          title="Comparer ton catalogue a celui de Glovo"
                        >
                          <Download
                            className={`w-4 h-4 ${exportEnCours === store.id ? "animate-pulse" : ""}`}
                          />
                          {exportEnCours === store.id
                            ? "Lecture chez Glovo..."
                            : "Concordance Glovo"}
                        </button>

                        {store.glovoStoreId && (
                          <button
                            onClick={() => handleSyncProducts(store)}
                            disabled={syncingStoreId === store.id}
                            className="text-primary hover:text-primary/90 hover:bg-primary/10 disabled:text-gray-400 disabled:cursor-not-allowed px-3 py-1.5 rounded-lg text-sm flex items-center gap-1.5 font-medium transition-colors duration-200 border border-primary/30"
                          >
                            <RefreshCw className={`w-4 h-4 ${syncingStoreId === store.id ? "animate-spin" : ""}`} />
                            {syncingStoreId === store.id ? "Synchronisation..." : "Sync produits Glovo"}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Overlay de synchronisation */}
      {syncingStoreId && (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-xl shadow-2xl p-6 md:p-7 w-full max-w-md border border-gray-100">
            <h2 className="text-lg font-semibold text-gray-900 mb-2">
              Synchronisation des produits
            </h2>
            <p className="text-sm text-gray-600 mb-4">
              {syncingStoreName
                ? `Nous synchronisons le catalogue Glovo pour le store « ${syncingStoreName} ».`
                : "Nous synchronisons le catalogue Glovo pour ce store."}
              <br />
              <span className="text-xs text-gray-500">
                Cette opération peut prendre quelques minutes selon le nombre de produits.
              </span>
            </p>
            <div className="space-y-3">
              <div className="h-2 w-full bg-gray-200 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-gradient-to-r from-primary to-emerald-400 transition-all duration-300 ease-out"
                  style={{ width: `${syncProgress.percentage}%` }}
                />
              </div>
              {syncProgress.status && (
                <p className="text-xs text-gray-600 font-medium">
                  {syncProgress.status}
                </p>
              )}
              <p className="text-xs text-gray-500">
                Merci de ne pas fermer cette page pendant la synchronisation.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Modales */}
      <ConfirmModal
        isOpen={deleteModal.isOpen}
        onClose={deleteModal.close}
        onConfirm={handleDeleteConfirm}
        title="Supprimer le Store"
        message={`Êtes-vous sûr de vouloir supprimer le store "${deleteStore?.name}" ? Cette action est irréversible.`}
        type="danger"
        confirmText="Supprimer"
      />

      <AlertModal
        isOpen={successModal.isOpen}
        onClose={successModal.close}
        title={modalMessage.title}
        message={modalMessage.message}
        type="success"
      />

      <AlertModal
        isOpen={errorModal.isOpen}
        onClose={errorModal.close}
        title={modalMessage.title}
        message={modalMessage.message}
        type="error"
      />
    </div>
  );
}
